import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import {
    ArrowLeft,
    Calendar,
    Share2,
    ThumbsUp,
    ThumbsDown,
    Clock,
    ChevronRight,
    Home,
    MessageSquare,
    BookOpen
} from 'lucide-react';
import { HelpArticle } from '../../data/helpData';
import { LandingNavbar } from '../landing/LandingNavbar';
import { LandingFooter } from '../landing/LandingFooter';

interface ArticleViewProps {
    article: HelpArticle;
    onBack: () => void;
    onGetStarted: () => void;
    onPrivacyPolicy?: () => void;
}

export const ArticleView: React.FC<ArticleViewProps> = ({ article, onBack, onGetStarted, onPrivacyPolicy }) => {
    // Fix scrolling and scroll to top
    useEffect(() => {
        document.body.style.overflow = 'auto';
        window.scrollTo(0, 0);
        return () => {
            document.body.style.overflow = '';
        };
    }, []);

    return (
        <div className="min-h-screen bg-slate-950 text-slate-200">
            <LandingNavbar onGetStarted={onGetStarted} />

            {/* Premium Breadcrumb Header */}
            <div className="pt-28 pb-10 px-6 max-w-5xl mx-auto">
                <div className="flex flex-wrap items-center gap-3 mb-10 overflow-hidden">
                    <button
                        onClick={onBack}
                        className="flex items-center gap-2 px-4 py-2 bg-slate-900 border border-slate-800 rounded-2xl text-xs font-black text-slate-400 hover:text-white hover:border-slate-700 transition-all uppercase tracking-widest"
                    >
                        <Home className="w-3 h-3" />
                        Help Hub
                    </button>
                    <ChevronRight className="w-4 h-4 text-slate-700" />
                    <span className="px-4 py-2 bg-slate-900/50 border border-slate-800/50 rounded-2xl text-xs font-black text-indigo-400 uppercase tracking-widest">
                        {article.category.replace('-', ' ')}
                    </span>
                </div>

                <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16 border-b-4 border-slate-900 pb-16">
                    <motion.div
                        className="max-w-3xl"
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                    >
                        <div className="flex items-center gap-4 text-slate-500 text-xs font-black uppercase tracking-[0.2em] mb-6">
                            <span className="flex items-center gap-1.5"><Calendar className="w-4 h-4" /> Updated {article.lastUpdated}</span>
                            <span className="w-1.5 h-1.5 rounded-full bg-slate-800" />
                            <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> 4 Minute Read</span>
                        </div>
                        <h1 className="text-5xl md:text-7xl font-black text-white tracking-tighter leading-[0.95]">
                            {article.title}
                        </h1>
                    </motion.div>

                    <button className="flex items-center gap-3 px-8 py-4 bg-white text-slate-950 rounded-2xl font-black text-sm uppercase tracking-widest hover:bg-slate-200 transition-all shadow-2xl">
                        <Share2 className="w-5 h-5" />
                        Share Doc
                    </button>
                </div>
            </div>

            <main className="max-w-5xl mx-auto px-6 pb-32">
                <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-16">
                    {/* Article Body */}
                    <article className="space-y-12">
                        <motion.p
                            className="text-2xl text-slate-100 font-medium leading-relaxed border-l-8 border-indigo-600 pl-10 py-2 italic"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.2 }}
                        >
                            {article.description}
                        </motion.p>

                        <motion.div
                            className="space-y-10"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.3 }}
                        >
                            {article.content.split('\n').map((line, i) => {
                                if (line.startsWith('# ')) {
                                    return <h1 key={i} className="text-5xl font-black pt-16 pb-6 text-white tracking-tight border-b-2 border-slate-900">{line.replace('# ', '')}</h1>;
                                }
                                if (line.startsWith('## ')) {
                                    return <h2 key={i} className="text-3xl font-black pt-12 pb-4 text-white tracking-tight">{line.replace('## ', '')}</h2>;
                                }
                                if (line.startsWith('### ')) {
                                    return <h3 key={i} className="text-2xl font-black pt-8 pb-2 text-indigo-400 tracking-tight uppercase tracking-[0.05em]">{line.replace('### ', '')}</h3>;
                                }
                                if (line.startsWith('- ')) {
                                    return (
                                        <div key={i} className="flex gap-4 items-start group">
                                            <div className="mt-2.5 w-1.5 h-1.5 rounded-full bg-indigo-500 shrink-0 group-hover:scale-150 transition-transform" />
                                            <p className="text-xl text-slate-300 leading-relaxed font-medium">
                                                {line.replace('- ', '')}
                                            </p>
                                        </div>
                                    );
                                }
                                if (line.trim() === '') return <div key={i} className="h-6" />;
                                return <p key={i} className="text-xl text-slate-300 leading-relaxed font-medium">{line}</p>;
                            })}
                        </motion.div>

                        {/* Article Footer Feedback */}
                        <div className="mt-24 p-12 bg-slate-900 rounded-4xl border-2 border-slate-800">
                            <h4 className="text-3xl font-black text-white mb-8 tracking-tighter text-center md:text-left">Was this guide helpful?</h4>
                            <div className="flex flex-col md:flex-row gap-6">
                                <button className="flex-1 flex items-center justify-center gap-3 px-8 py-5 rounded-2xl bg-slate-950 border-2 border-slate-800 hover:border-indigo-500 hover:text-indigo-400 transition-all font-black uppercase tracking-widest text-xs group">
                                    <ThumbsUp className="w-5 h-5 group-hover:-translate-y-1 transition-transform" />
                                    Perfectly Clear
                                </button>
                                <button className="flex-1 flex items-center justify-center gap-3 px-8 py-5 rounded-2xl bg-slate-950 border-2 border-slate-800 hover:border-red-500 hover:text-red-400 transition-all font-black uppercase tracking-widest text-xs group">
                                    <ThumbsDown className="w-5 h-5 group-hover:translate-y-1 transition-transform" />
                                    Needs More Detail
                                </button>
                            </div>
                        </div>
                    </article>

                    {/* Sidebar TOC / Sidebar Content */}
                    <aside className="hidden lg:block space-y-12 h-fit sticky top-32">
                        <section className="p-8 bg-slate-900 rounded-3xl border border-slate-800/50">
                            <h5 className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-500 mb-6">Article Overview</h5>
                            <div className="space-y-4">
                                <div className="p-4 bg-slate-950 rounded-2xl border border-slate-800/50 flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-xl bg-indigo-500/10 flex items-center justify-center text-indigo-500">
                                        <BookOpen className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <p className="text-xs text-slate-300 font-black uppercase leading-none mb-1">Status</p>
                                        <p className="text-xs text-indigo-400 font-bold uppercase tracking-tighter">Verified Doc</p>
                                    </div>
                                </div>
                            </div>
                        </section>

                        <section className="p-8 space-y-4">
                            <h5 className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-500 mb-6">Need support?</h5>
                            <p className="text-sm text-slate-400 font-medium leading-relaxed">Can't find what you're looking for?</p>
                            <button className="w-full py-4 px-6 bg-slate-900 border border-slate-800 rounded-2xl text-xs font-black text-white hover:bg-slate-800 transition-colors uppercase tracking-widest flex items-center justify-center gap-2">
                                <MessageSquare className="w-4 h-4 text-indigo-500" />
                                Chat with Us
                            </button>
                        </section>
                    </aside>
                </div>
            </main>

            <LandingFooter onPrivacyPolicy={onPrivacyPolicy} />
        </div>
    );
};
